<template>
  <div
    :class="[
      !noHover && $style.wrapper,
      noHover && $style.wrapperNoHover,
    ]"
    class="w-100"
    @click="openMatchDetail"
  >
    <b-container>
      <TheMatchHeader
        :start-date="match.start"
        :status="match.status"
        :team1="match.team1"
        :team2="match.team2"
        :score-team1="match.score_team1"
        :score-team2="match.score_team2"
      />
      <b-row class="p-2">
        <template v-if="match.status === matchStatuses.upcoming">
          <BaseLoading
            v-if="isLoading && !team1Players.length && !team2Players.length"
            :min-height="50"
          />
          <template>
            <b-col :cols="12">
              <hr>
            </b-col>
            <b-col
              :cols="12"
              :md="6"
            >
              <transition-group
                tag="div"
                enter-active-class="fadeInDown"
                leave-active-class="fadeOutDown"
                :duration="{ enter: 1000, leave: 400 }"
                mode="out-in"
              >
                <div
                  v-for="(player,index) in team1Players"
                  :key="`topPlayer-${player.id}`"
                  class="py-1"
                  :style="{ animationDelay: `${index * 0.05}s`}"
                >
                  <ThePickAthleteCard
                    :id="player.id"
                    :round-id="match.round_id"
                    :name="player.name"
                    :avatar="player.image"
                    :team="player.team"
                    :position="player.position"
                    :score="player.score"
                    :is-selected="!! selectedAthletes.find(({id}) => player.id === id)"
                    :is-player-select-enabled="pickedPlayersCount < maximumPickedPlayersCount"
                    :maximum-picked-players-count="maximumPickedPlayersCount"
                    @onAthletePick="handleAthletePick"
                  />
                </div>
              </transition-group>
            </b-col>
            <b-col
              :cols="12"
              :md="6"
            >
              <transition-group
                tag="div"
                enter-active-class="fadeInDown"
                leave-active-class="fadeOutDown"
                :duration="{ enter: 1000, leave: 400 }"
                mode="out-in"
              >
                <div
                  v-for="(player,index) in team2Players"
                  :key="`topPlayer-${player.id}`"
                  class="py-1"
                  :style="{ animationDelay: `${index * 0.05}s`}"
                >
                  <ThePickAthleteCard
                    :id="player.id"
                    :round-id="match.round_id"
                    :name="player.name"
                    :avatar="player.image"
                    :team="player.team"
                    :position="player.position"
                    :score="player.score"
                    :is-selected="!! selectedAthletes.find(({id}) => player.id === id)"
                    :is-player-select-enabled="pickedPlayersCount < maximumPickedPlayersCount"
                    :maximum-picked-players-count="maximumPickedPlayersCount"
                    button-position="left"
                    @onAthletePick="handleAthletePick"
                  />
                </div>
              </transition-group>
            </b-col>
          </template>
        </template>
        <template v-else>
          <BaseLoading
            v-if="isLoading"
            :min-height="50"
          />
          <template v-else>
            <template v-if="!team1Players.length && !team2Players.length">
              <b-col :cols="12">
                <BaseAlert variant="info">
                  {{ $t('no-events-in-match-yet') }}
                </BaseAlert>
              </b-col>
            </template>
            <template v-else>
              <b-col :cols="12">
                <hr>
              </b-col>
              <b-col :cols="6">
                <div
                  v-for="player in team1Players"
                  :key="`topPlayer-${player.id}`"
                  class="d-flex align-items-center py-1"
                >
                  <BaseAvatar
                    class="d-flex align-items-center pr-2"
                    :url="player.athlete.image"
                    :size="30"
                  />
                  <div class="d-flex flex-column">
                    <span :class="$style.playerNameWrapper">{{ player.athlete.lastName[$i18n.locale] }}</span>
                    <span :class="$style.playerScore">
                      <span>{{ $t('x-minute-of-match', { minute: player.time }) }}</span>
                      <span>{{ $tc(`athlete.performance.${player.category.name}`, player.value) }}</span>
                    </span>
                  </div>
                </div>
              </b-col>
              <b-col
                :cols="6"
                class="d-flex flex-column align-items-end text-right"
              >
                <div
                  v-for="player in team2Players"
                  :key="`topPlayer-${player.id}`"
                  class="d-flex align-items-center py-1"
                >
                  <div class="d-flex flex-column">
                    <span :class="$style.playerNameWrapper">{{ player.athlete.lastName[$i18n.locale] }}</span>
                    <span :class="$style.playerScore">
                      <span>{{ $t('x-minute-of-match', { minute: player.time }) }}</span>
                      <span>{{ $tc(`athlete.performance.${player.category.name}`, player.value) }}</span>
                    </span>
                  </div>
                  <BaseAvatar
                    class="d-flex align-items-center pl-2"
                    :url="player.athlete.image"
                    :size="30"
                  />
                </div>
              </b-col>
            </template>
          </template>
        </template>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters, mapState } from 'vuex'
import { MATCH_STATUSES } from '@/store/modules/matches/matchDetail'
import BaseAvatar from '@/components/Atoms/BaseAvatar'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseAlert from '@/components/Atoms/BaseAlert'
import TheMatchHeader from '@/components/Molecules/TheMatchHeader'
import ThePickAthleteCard from '@/components/Molecules/ThePickAthleteCard'

export default {
  name: 'TheMatchDetailRow',
  components: {
    TheMatchHeader,
    BaseAlert,
    BaseLoading,
    BaseAvatar,
    ThePickAthleteCard,
  },
  props: {
    match: {
      type: Object,
      required: true,
    },
    maximumPickedPlayersCount: {
      type: Number,
      required: false,
      default: 8,
    },
    leagueType: {
      type: String,
      required: false,
      default: null,
    },
    noHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    noMatchDetail: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      matchStatuses: MATCH_STATUSES,
    }
  },
  computed: {
    ...mapState({
      isMatchAthletesPerformanceLoading: state => state['matches/matchAthletePerformance'].isMatchAthletesPerformanceLoading,
    }),
    ...mapGetters({
      getScorePlayers: 'teams/topScorers/getTeamTopScorers',
      isTopScorerLoading: 'teams/topScorers/getIsTeamTopScorersLoading',
      getMatchPerformance: 'matches/matchAthletePerformance/getMatchAthletesPerformanceByMatchId',
      isMatchPerformanceLoading: 'matches/matchAthletePerformance/getIsMatchPerformanceLoading',
    }),
    isLoading() {
      if (this.match.status === this.matchStatuses.upcoming) {
        return this.isTopScorerLoading(this.match.team1.id) || this.isTopScorerLoading(this.match.team2.id)
      } else {
        return this.isMatchPerformanceLoading(this.match.slug)
      }
    },
    team1Players() {
      if (this.match.status === this.matchStatuses.upcoming) {
        return this.getScorePlayers(this.match.team1.id)
      } else {
        return this.getMatchPerformance(this.match.slug)
          .filter(({ athlete }) => athlete.team.id === this.match.team1.id)
      }
    },
    team2Players() {
      if (this.match.status === this.matchStatuses.upcoming) {
        return this.getScorePlayers(this.match.team2.id)
      } else {
        return this.getMatchPerformance(this.match.slug)
          .filter(({ athlete }) => athlete.team.id === this.match.team2.id)
      }
    },
    selectedAthletes() {
      return this.$store.getters['rounds/athletesInRound/getSelectedAthletesForRound'](this.match.round_id)
    },
    pickedPlayersCount() {
      return this.selectedAthletes.length
    },
  },
  created() {
    if (this.match.status === this.matchStatuses.upcoming) {
      this.fetchTeamTopScorers({
        teamId: this.match.team1.id,
      })
      this.fetchTeamTopScorers({
        teamId: this.match.team2.id,
      })
    } else {
      this.fetchMatchAthletesPerformance({ matchId: this.match.slug })
    }
  },
  methods: {
    ...mapActions({
      fetchTeamTopScorers: 'teams/topScorers/fetchTeamTopScorers',
      fetchMatchAthletesPerformance: 'matches/matchAthletePerformance/fetchMatchAthletesPerformance',
      fetchSelectedAthletes: 'rounds/athletesInRound/fetchSelectedAthletes',
      openSummerTeamCompleteModal: 'rounds/summer/predictionCompleted/openModal',
      openTeamCompleteModal: 'profile/inviteUrl/openTeamCompleteModal',
      incrementPredictionCount: 'auth/incrementPredictionCount',
    }),
    openMatchDetail() {
      if (this.noMatchDetail) { return }
      const [ league, match ] = this.match.slug.split('/')
      this.$router.push({
        name: 'match',
        params: {
          league,
          match,
        },
      })
    },
    handleAthletePick() {
      this.fetchSelectedAthletes({
        roundId: this.match.round_id,
      }).then(({ athletes }) => {
        if (athletes.length === this.maximumPickedPlayersCount) {
          if (this.leagueType === 'summer') {
            this.openSummerTeamCompleteModal({
              roundId: this.match.round_id,
            })
          } else {
            this.openTeamCompleteModal({
              roundId: this.match.round_id,
            })
          }
          this.incrementPredictionCount()
          VueScrollTo.scrollTo('#content', 500, {
            container: 'body',
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" module>
  @import "../../assets/scss/variables";
  .wrapper {
    border-bottom: 1px solid $color-silver-charm;
    cursor: pointer;
    transition: all .3s ease-in-out;
    background-color: $color-white;
    &:hover {
      background-color: $color-bleached-silk;
    }
    .playerNameWrapper {
      color: $color-midnight-blue;
      font-size: $font-size-small;
    }
    .playerScore {
      font-size: $font-size-tiny;
      display: flex;
      flex-direction: column;
    }
  }
 .wrapperNoHover {
    border-bottom: 1px solid $color-silver-charm;
    transition: all .3s ease-in-out;
    background-color: $color-white;
  }
</style>
